import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatOptionSelectionChange } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import { MatSelectChange } from "@angular/material/select";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { TableColumn } from "@swimlane/ngx-datatable";
import { ApplicationInfoDto, BomDto, BomItemDto, CostCentreDto, CustomerContactListItemDto, CustomerResponse, DealCategory, DealDto, DealQuoteCalibrationLineDto, DealQuoteHireLineDto, DealQuoteMarineLineDto, DealQuotePartLineDto, DealQuoteProductLineDto, DealStageDto, DealType, DlmProductDto, EnumViewModel, FreightTerm, Incoterm, LeadTimeType, LoadcellResponse, ManufactureProcessCategoryBreakdownDto, ManufactureProcessDto, MarinePartDto, OrderDirection, PartResponse, PartUnitType, ProductDto, PurchaseOrderDto, PurchaseOrderProductItemDto, QuoteFileDto, TeamRole, TransportMode, UserDto, VatOption } from "app/api/models";
import { BomService, CostCentreService, CustomerService, DealService, DlmProductService, EmployeeService, ManufactureProcessService, PartService, PickSheetService, ProductService, PurchaseOrderService, RepairsAndCalibrationsService, SettingsService } from "app/api/services";
import { PurchaseOrderBuilderComponent } from "app/views/admin/pages/purchase-orders/purchase-order-builder/purchase-order-builder.component";
import { AppAuthService } from "app/api/services/cookie-jwt-auth/app-auth.service";
import { getUniqueOnSelf, sortOnProperty, sumList } from "app/app.component";
import { AddItemDialogComponent } from "app/core/components/add-item-dialog/add-item-dialog.component";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import { GenericInfoDialogService } from "app/core/services/generic-info-dialog/infoDialog.service";
import { TimeUtil } from "app/core/utils/time-utils";
import { UploadFilePopupComponent } from "app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component";
import { AssemblyProcessPopupComponent } from "app/views/admin/pages/product-register/boms/bom-builder/assembly-process-popup/assembly-process-popup.component";
import { Paging } from "app/views/calibrations/shared/components/paging";
import { CellInformationDialogComponent } from "app/views/calibrations/views/repairs-and-calibrations/cell-information-dialog/cell-information-dialog.component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable, { MarginPaddingInput, RowInput, Styles, UserOptions } from "jspdf-autotable";
import { isNumber } from "lodash";
import { forkJoin, mapTo, of, tap } from "rxjs";

@Component({
    selector       : 'quote-wizard-dialog',
    templateUrl    : './quote-wizard-dialog.component.html',
    styleUrls      : ['./quote-wizard-dialog.component.scss'],
})
export class QuoteWizardDialogComponent extends Paging implements OnInit
{
    public dealId: number;
    public deal: DealDto;
    public customerForm: FormGroup;
    public productsForm: FormGroup;
    public summaryForm: FormGroup;
    
    public files: File[] = [];
    public fileInfos: QuoteFileDto[] = [];

    public isLinear: boolean = true;
    public selectedStepIndex = 0;

    public purchaseOrders: PurchaseOrderDto[] = [];
    public allUsers: UserDto[] = [];
    public parts: PartResponse[] = [];
    public filteredParts: PartResponse[] = [];
    public products: DlmProductDto[] = [];
    public filteredProducts: DlmProductDto[] = [];
    public loadcells: LoadcellResponse[] = [];
    public filteredLoadcells: LoadcellResponse[] = [];
    public assets: ProductDto[] = [];
    public filteredAssets: ProductDto[] = [];
    public marineParts: MarinePartDto[] = [];
    public filteredMarineParts: MarinePartDto[] = [];
    public marinePartCategories: string[] = [];
    public filteredMarinePartCategories: string[] = [];

    public companies: CustomerResponse[] = [];
    public filteredCompanies: CustomerResponse[] = [];
    selectedCompanyName: string = '';
    public contacts: CustomerContactListItemDto[] = [];
    public stages: DealStageDto[] = [];
    
    public manufactureProcesses : ManufactureProcessCategoryBreakdownDto[] = [];
    public selectedManufactureCategoryId: number;
    public selectedManufactureProcessId: number;
    public selectedProcess: ManufactureProcessDto;
    public boms: BomDto[] = [];

    public appInfo: ApplicationInfoDto;

    public categories = Object.values(DealCategory).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getCategoryName(+x), value: <DealCategory>x});
    public incoterms = Object.values(Incoterm).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getIncotermName(+x), value: <Incoterm>x});
    public freightTerms = Object.values(FreightTerm).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getFreightTermName(+x), value: <FreightTerm>x});
    public transportModes = Object.values(TransportMode).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getTransportModeName(+x), value: <TransportMode>x});
    public leadTimeTypes = Object.values(LeadTimeType).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getLeadTimeTypeName(+x), value: <LeadTimeType>x});
    
    public slaDate: Date = null;
    public now: Date = new Date();

    public marginTypes: EnumViewModel[] = [
        { value: 0, name: 'No Margin (Cost Price)' },
        // { value: 1, name: 'Sell at Margin %' },
        { value: 2, name: 'Charge Margin %' },
    ];

    public marginValues: number[] = [0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5];
    
    public vatRates: EnumViewModel[] = [
        { value: 0, name: '0% - Zero % Rated' },
        { value: 0.05, name: '5% - Reduced Rate' },
        { value: 0.2, name: '20% - Standard Rate' },
    ];

    public DealType = DealType;

    constructor(
        public dialogRef: MatDialogRef<QuoteWizardDialogComponent>,
        private partService: PartService,
        private repairService: RepairsAndCalibrationsService,
        private purchaseOrderService: PurchaseOrderService,
        private pickSheetService: PickSheetService,
        private dlmProductService: DlmProductService,
        private assetService: ProductService,
        private customerService: CustomerService,
        private dealService: DealService,
        private employeeService: EmployeeService,
        private bomService: BomService,
        private settingsService: SettingsService,
        private manufactureProcessService: ManufactureProcessService,
        private costCentreService: CostCentreService,
        private _formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private datePipe: DatePipe,
        private currencyPipe: CurrencyPipe,
        private dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _infoDialogService: GenericInfoDialogService,
        private loader: AppLoaderService,
        private appAuth: AppAuthService,
        @Inject(MAT_DIALOG_DATA) public data: {deal: DealDto}
    )
    {
        super();
    }

    ngOnInit(): void
    {
        //reorder cats
        var repeatCat = this.categories.find(x => x.value == DealCategory.RepeatCustomProduct);
        this.categories = this.categories.filter(x => x.value != DealCategory.RepeatCustomProduct);
        this.categories.splice(1, 0, repeatCat);

        this.dealId = this.data.deal?.dealId ?? null;
        this.deal = this.data.deal ?? null;

        this.searchTerm = '';
        this.page.totalPages = 2;

        this.loader.open();
        forkJoin({
            a: this.loadParts(),
            b: this.loadProducts(),
            c: this.loadCompanies(),
            d: this.loadContacts(),
            e: this.loadUsers(),
            f: this.loadStages(),
            g: this.loadAppInfo(),
            h: this.loadManufactureProcesses(),
            i: this.loadLoadcells(),
            j: this.loadAssets(),
            k: this.loadMarineParts(),
        }).subscribe({
            next: () => {
                this.loader.close();
                this.loadDeal();
            },
            error: () => {
                this.loader.close();
            }
        });

        // this.loadPurchaseOrders();
        this.loadCostCentres();
    }

    loadCompanies() {
        return this.customerService.apiCustomerSearchAllGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.companies = res.body.data;
            this.filteredCompanies = this.companies;
        }),
        mapTo(void 0));
    }

    loadContacts() {
        return this.customerService.apiCustomerSearchContactsGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.contacts = res.body.data;
        }),
        mapTo(void 0));
    }

    loadStages() {
        return this.dealService.apiDealStagesGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.stages = res.body.data;
        }),
        mapTo(void 0));
    }

    loadDeal() {
        if(!this.dealId) {
            this.dealService.apiDealNextNonWonQuoteRefGet$Json$Response().subscribe(res => {
                if(!res?.body?.message) return;
                this.deal = { quoteRef: res.body.message };
                this.regenTitle();
            });

            this.buildForm();
            return;
        }

        this.dealService.apiDealDealGet$Json$Response({dealId: this.dealId}).subscribe(res => {
            if(!res.body.data) return;

            this.deal = res.body.data;
            console.log("got deal", this.deal);

            //calculate SLA date
            this.calculateSLA();

            //pull in BOM
            if(this.deal.bomId) {
                this.bomService.apiBomBomGet$Json$Response({bomId: this.deal.bomId}).subscribe(res => {
                    if(!res?.body?.data) return;
                    this.bom = res.body.data;
                    this.bomAssemblyProcesses = (this.bom as any).assemblyProcesses || [];
                    this.customProductName = this.lookupProductById(this.bom.productId)?.description ?? '';
                    this.customProductPartNumber = this.lookupProductById(this.bom.productId)?.partNumber ?? '0001-9999';
                });
            }

            // this should only happen for legacy quotes with no ref
            if(!this.deal.quoteRef)
                this.dealService.apiDealQuoteRefGet$Json$Response({dealId: this.dealId}).subscribe(res => {
                    if(!res?.body?.message) return;
                    this.deal.quoteRef = res.body.message;
                    this.regenTitle();
                });

            //load process and set selected values
            if(this.deal.manufactureColumnDefinitionId) {
                this.loadChosenManufactureProcess(this.deal.manufactureColumnDefinitionId);
                
                //set selected values
                this.selectedManufactureProcessId = this.manufactureProcesses
                    .flatMap(x => x.processes)
                    .find(x => x.columnDefinitions.some(c => c.manufactureProcessColumnDefinitionId == this.deal.manufactureColumnDefinitionId))?.manufactureProcessId;
                this.selectedManufactureCategoryId = this.manufactureProcesses
                    .find(x => x.processes.some(p => p.manufactureProcessId == this.selectedManufactureProcessId))?.manufactureProcessCategoryId;
            }
        
            this.buildForm();
            this.pageProducts();
            this.loadFiles();

            //if this is an unfinished custom quote, go to step 3
            if(this.deal.isWizardInProgress && this.deal.isCustomQuote && !this.deal.isWizardInFirstStage) {
                this.isLinear = false;
                this.selectedStepIndex = 2;
            }
        });
    }

    loadPurchaseOrders() {
        this.purchaseOrderService.apiPurchaseOrderGetAllGet$Json$Response({isSupplier: false}).subscribe(res => {
            if(!res?.body?.data) return;
            
            this.purchaseOrders = res.body.data.filter(x => !x.isDraft);
            this.cdr.detectChanges();
        });
    }

    loadCostCentres() {
        this.costCentreService.apiCostCentreCostCentresGet$Json$Response().subscribe((res) => {
            if (!res?.body?.data) return;

            this.costCentres = res.body.data;
            this.cdr.detectChanges();
        });
    }
    
    loadUsers() {
        return this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response({archived: false}).pipe(tap(res => {
            if(!res?.body.data) return;
            this.allUsers = res.body.data;
        }),
        mapTo(void 0));
    }

    public loadAppInfo() {
        return this.settingsService.apiSettingsAppInfoGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.appInfo = res.body.data;
        }),
        mapTo(void 0));
    }

    loadProducts() {
        return this.dlmProductService.apiDlmProductGetProductsGet$Json$Response().pipe(tap(res => {
            if (!res?.body?.data) return;

            this.products = res.body.data;
            this.pageProducts();
        }),
        mapTo(void 0));
    }

    loadParts() {
        return this.partService.apiPartPartsGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.parts = res.body.data;

            //no longer paged after switching to BOM view for custom products
            // this.pageParts();
            this.filteredParts = this.parts;
            
            // refresh form array values so displayWith starts working
            const fa = this.partItems;
            fa?.setValue(fa.value);
            fa?.updateValueAndValidity({ emitEvent: false });
            this.cdr.detectChanges();
        }),
        mapTo(void 0));
    }

    loadLoadcells() {
        return this.repairService.apiRepairsAndCalibrationsLoadcellsGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.loadcells = sortOnProperty(res.body.data, 'bookingRef');
            this.pageLoadcells();
        }),
        mapTo(void 0));
    }

    loadAssets() {
        return this.assetService.apiProductGetAllGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.assets = sortOnProperty(res.body.data, 'name');
            this.pageAssets();
        }),
        mapTo(void 0));
    }

    loadMarineParts() {
        return this.dealService.apiDealMarinePartsGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;

            this.marineParts = res.body.data;
            this.filteredMarineParts = this.marineParts;
            this.marinePartCategories = getUniqueOnSelf(this.marineParts.map(x => x.category));
            this.filteredMarinePartCategories = this.marinePartCategories;
        }),
        mapTo(void 0));
    }

    loadManufactureProcesses() {
        return this.manufactureProcessService.apiManufactureProcessGetCategoriesBreakdownGet$Json$Response().pipe(tap(res => {
            if(!res?.body?.data) return;
            this.manufactureProcesses = res.body.data;
        }),
        mapTo(void 0));
    }
    
    loadChosenManufactureProcess(columnDefinitionId: number) {
        if(!columnDefinitionId) return;

        //fetch the process
        this.manufactureProcessService.apiManufactureProcessGetProcessGet$Json$Response({processId: this.selectedManufactureProcessId}).subscribe(res => {
            if(!res?.body?.data) return;

            var process = res.body.data;
            process.columnDefinitions = null;

            //get steps specifically for the column chosen
            for(var stage of process.stages)
                for(var step of stage.steps) {
                    step['value'] = step.values.find(x => x.columnDefinitionId == columnDefinitionId)?.value;
                    step.values = null;
                }

            console.log("process", process);
            this.selectedProcess = process;
        });
    }

    getBomForProduct(productId: number) {
        var bom = this.boms.find(x => x.productId == productId);

        if(bom)
            return of(null);
        else
            return this.bomService.apiBomBomByProductIdGet$Json$Response({productId: productId}).pipe(tap(res => {
                    if(!res?.body?.data) return null;
                    this.boms.push(res.body.data);
                },
            ),
            mapTo(void 0));
    }
    
    getManufactureProcesses = () => this.manufactureProcesses?.find(x => x.manufactureProcessCategoryId == this.selectedManufactureCategoryId)?.processes ?? [];
    getManufactureColumns = () => this.getManufactureProcesses()?.find(x => x.manufactureProcessId == this.selectedManufactureProcessId)?.columnDefinitions ?? [];
    getManufactureProcessTotal = () => sumList(this.selectedProcess?.stages.flatMap(stage => sumList(stage.steps.map(x => +x['value']))) ?? []);
    getHoursText = (x: number) => TimeUtil.getHoursText(x);

    manufactureCategoryChanged(event: any) {
        this.selectedManufactureCategoryId = event.value;
        this.selectedManufactureProcessId = null;
        this.selectedProcess = null;
        this.customerForm.controls['manufactureColumnDefinitionId'].setValue(null);
    }
    manufactureProcessChanged(event: any) {
        this.selectedManufactureProcessId = event.value;
        this.selectedProcess = null;
        this.customerForm.controls['manufactureColumnDefinitionId'].setValue(null);
    }


    pageProducts(setManualRes: boolean = false) {
        var searchFiltered = this.products.filter(m => this.isRepeatCustomProduct() == m.isCustomProduct && m.partNumber?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredProducts = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);

        if(setManualRes)
            this.setPageInfoFromManualRes(this.products);
    }

    pageParts(setManualRes: boolean = false) {
        var searchFiltered = this.parts.filter(m => m.number?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredParts = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.parts);
    }

    pageLoadcells(setManualRes: boolean = false) {
        var searchFiltered = this.loadcells.filter(m => m.serialNumber?.toLowerCase().includes(this.searchTerm.toLowerCase()) || m.bookingRef?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredLoadcells = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.loadcells);
    }

    pageAssets(setManualRes: boolean = false) {
        var searchFiltered = this.assets.filter(m => m.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) || m.serialNumber?.toLowerCase().includes(this.searchTerm.toLowerCase()));
        this.filteredAssets = searchFiltered.slice(0, (this.page.pageIndex + 1) * this.page.pageSize + 1);
        
        if(setManualRes)
            this.setPageInfoFromManualRes(this.assets);
    }
    
    onScroll(event: any) {
        if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 2) { // -2 is for cross-browser issue(?) where in rare cases scroll isn't detected but only on Mitch's chrome
            console.log("Scrolled to end of list");

            if(this.page.pageIndex < this.page.totalPages && this.page.totalPages > 1) {
                this.page.pageIndex++;
                console.log("scroll - get more items - page", this.page.pageIndex);

                if(this.isCustomQuote())
                    this.pageParts(true);
                else if (this.getDealType() == DealType.Calibration)
                    this.pageLoadcells(true);
                else if (this.getDealType() == DealType.Hire)
                    this.pageAssets(true);
                else
                    this.pageProducts(true);
            }
        }
    }
    
    buildForm() {
        this.customerForm = this._formBuilder.group({
            name                : [this.deal?.name ?? '',               Validators.required],
            customerReqNo       : [this.deal?.customerReqNo ?? '',      Validators.required],
            // stageId             : [this.item?.stageId ?? '',            Validators.required],
            // sentDate            : [this.item?.sentDate ?? ''],
            // closeDate           : [this.deal?.closeDate ?? ''],
            companyId           : [this.deal?.companyId ?? null,          Validators.required],
            contactId           : [this.deal?.contactId ?? null,          Validators.required],
            invoiceAddressId    : [this.deal?.invoiceAddressId ?? null],
            
            // value               : [this.item?.value ?? '',              Validators.required],
            // partsDescription    : [this.item?.partsDescription ?? ''],
            dealCategories      : [this.deal?.dealCategories ?? []],
            // comments            : [this.deal?.comments ?? ''],
            // userId              : [this.deal?.userId ?? null,             Validators.required],

            isCustomQuote           : [this.deal?.isCustomQuote ?? false],
            isRepeatCustomProduct   : [this.deal?.isRepeatCustomProduct ?? false],
            dealType                : [this.deal?.dealType ?? DealType.Standard],

            expectedCloseDate   : [this.deal?.expectedCloseDate ?? null,    Validators.required],
            notes               : [this.deal?.notes ?? ''],

            manufactureColumnDefinitionId : [this.deal?.manufactureColumnDefinitionId ?? null],
        });

        this.productsForm = this._formBuilder.group({
            productLines        : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => [DealType.Standard, DealType.RepeatCustom].includes(this.getDealType()))),
            partLines           : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.isCustomQuote())),

            calibrationLines    : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Calibration)),
            hireLines           : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Hire)),
            marineLines         : this._formBuilder.array([], (c: FormArray) => this.itemsValidator(c, () => this.getDealType() == DealType.Marine)),
        });
        
        this.summaryForm = this._formBuilder.group({
            marginType          : [!this.deal?.margin ? 0 : (this.deal?.margin > 100 ? 2 : 1)],
            margin              : [this.deal?.margin ?? 0,              [(c: FormGroup) => this.singleValidator(c, () => this.hasMargin(), () => this.marginOver15())]],
            incoterm            : [this.deal?.incoterm ?? null,         Validators.required],
            freightTerm         : [this.deal?.freightTerm ?? null],
            transportMode       : [this.deal?.transportMode ?? null],
            freightCost         : [this.deal?.freightCost ?? 0],
            vatRate             : [this.deal?.vatRate ?? 0,             Validators.required],
        });

        if(this.summaryForm.controls['marginType'].value == 0)
            this.summaryForm.controls['margin'].disable({emitEvent: false}); //prevents the "min" errors from the input

        this.deal?.quoteProductLines?.forEach((item: DealQuoteProductLineDto) => this.addProductItem(item));
        this.deal?.quotePartLines?.forEach((item: DealQuotePartLineDto) => this.addPartItem(item));
        this.deal?.quoteCalibrationLines?.forEach((item: DealQuoteCalibrationLineDto) => this.addCalibrationItem(item));
        this.deal?.quoteHireLines?.forEach((item: DealQuoteHireLineDto) => this.addHireItem(item));
        this.deal?.quoteMarineLines?.forEach((item: DealQuoteMarineLineDto) => this.addMarineItem(item));

        if(this.deal?.companyId && this.companies?.length)
            this.selectedCompanyName = this.getCompany()?.name;

        this.cdr.detectChanges();
    }

    hasMargin = () => this.summaryForm?.controls['marginType'].value > 0;
    marginOver15 = () => (this.summaryForm?.controls['margin'].value || 0) >= 15;

    itemsValidator(control: FormArray, requiredFn: () => boolean): { [key: string]: boolean } | null {
        return (requiredFn() && control.length < 1) ? { 'noItems': true } : null;
    }
    
    singleValidator(control: FormGroup, requiredFn: () => boolean, validFn: () => boolean): { [key: string]: boolean } | null {
        return (requiredFn() && !validFn()) ? { 'invalidItem': true } : null;
    }

    get productItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('productLines') as FormArray : null;
    }
    get partItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('partLines') as FormArray : null;
    }
    get calibrationItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('calibrationLines') as FormArray : null;
    }
    get hireItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('hireLines') as FormArray : null;
    }
    get marineItems(): FormArray {
        return this.productsForm ? this.productsForm?.get('marineLines') as FormArray : null;
    }

    addProduct(product: DlmProductDto) {
        //get bom
        this.getBomForProduct(product.dlmProductId).subscribe(_ => {
            //get lead time
            var bom = this.boms.find(x => x.productId == product.dlmProductId);
            var leadTime = sumList(bom?.assemblyProcesses?.map(x => (x.setupHours ?? 0) * (x.isSubcontracted ? 35 : 1) + (x.runPerUnitHours ?? 0)) ?? []);

            this.addProductItem({
                productId: product.dlmProductId,
                cost: this.lookupProductById(product.dlmProductId)?.price,
                leadTime: TimeUtil.getWeeksNumber(leadTime),
                leadTimeType: LeadTimeType.Weeks,
            });
        });

    }
    addPart = (part: PartResponse) => this.addPartItem({
        partId: part.id,
        cost: this.lookupPartById(part.id)?.cost,
    });
    addLoadcell = (loadcell: LoadcellResponse) => this.addCalibrationItem({
        loadcellId: loadcell.loadcellId,
    });
    addAsset = (asset: ProductDto) => this.addHireItem({
        productId: asset.productId,
        cost: asset.cost,
    });


    addProductItem(existingItem: DealQuoteProductLineDto = null) {
        this.productItems?.push(this.newProductItem(existingItem));
        this.cdr.detectChanges();
    }
    addPartItem(existingItem: DealQuotePartLineDto = null) {
        this.partItems?.push(this.newPartItem(existingItem));
        this.cdr.detectChanges();
    }
    addCalibrationItem(existingItem: DealQuoteCalibrationLineDto = null) {
        this.calibrationItems?.push(this.newCalibrationItem(existingItem));
        this.cdr.detectChanges();
    }
    addHireItem(existingItem: DealQuoteHireLineDto = null) {
        this.hireItems?.push(this.newHireItem(existingItem));
        this.cdr.detectChanges();
    }
    addMarineItem(existingItem: DealQuoteMarineLineDto = null) {
        this.marineItems?.push(this.newMarineItem(existingItem));
        this.cdr.detectChanges();
    }

    newProductItem(existingItem: DealQuoteProductLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteProductLineId  : [ existingItem?.quoteProductLineId ?? null ],
            productId           : [ existingItem?.productId ?? null,    Validators.required],
            quantity            : [ existingItem?.quantity ?? 1,        [Validators.required, Validators.min(1)]],
            discount            : [ existingItem?.discount ?? 0],
            cost                : [ existingItem?.cost ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newPartItem(existingItem: DealQuotePartLineDto = null): FormGroup {
        return this._formBuilder.group({
            quotePartLineId     : [ existingItem?.quotePartLineId ?? null ],
            partId              : [ existingItem?.partId ?? null,       Validators.required],
            quantity            : [ existingItem?.quantity ?? 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
            level               : [ existingItem?.level ?? this.getNextLevel().toString(),  Validators.required],
            hidden              : [ false ],
        });
    }
    newCalibrationItem(existingItem: DealQuoteCalibrationLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteCalibrationLineId  : [ existingItem?.quoteCalibrationLineId ?? null ],
            description         : [ existingItem?.description ?? '',    Validators.required],
            notes               : [ existingItem?.notes ?? ''],
            loadcellId          : [ existingItem?.loadcellId ?? null,   Validators.required],
            quantity            : [ existingItem?.quantity ?? 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newHireItem(existingItem: DealQuoteHireLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteHireLineId     : [ existingItem?.quoteHireLineId ?? null ],
            productId           : [ existingItem?.productId ?? null,        Validators.required],
            hireFrom            : [ existingItem?.hireFrom ?? new Date(),   Validators.required],
            hireTo              : [ existingItem?.hireTo ?? null],
            cost                : [ existingItem?.cost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }
    newMarineItem(existingItem: DealQuoteMarineLineDto = null): FormGroup {
        return this._formBuilder.group({
            quoteMarineLineId   : [ existingItem?.quoteMarineLineId ?? null ],
            partName            : [ existingItem?.partName ?? '',       Validators.required],
            supplier            : [ existingItem?.supplier ?? '',       Validators.required],
            category            : [ existingItem?.category ?? ''],
            quantity            : [ existingItem?.quantity ?? 1,        [Validators.required, Validators.min(1)]],
            cost                : [ existingItem?.cost ?? 0],
            internalCost        : [ existingItem?.internalCost ?? 0],
            discount            : [ existingItem?.discount ?? 0],
            leadTime            : [ existingItem?.leadTime ?? ''],
            leadTimeType        : [ existingItem?.leadTimeType ?? LeadTimeType.Weeks ],
        });
    }

    deleteProductItem(index: number) {
        this.productItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deletePartItem(index: number) {
        this.partItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteCalibrationItem(index: number) {
        this.calibrationItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteHireItem(index: number) {
        this.hireItems?.removeAt(index);
        this.cdr.detectChanges();
    }
    deleteMarineItem(index: number) {
        this.marineItems?.removeAt(index);
        this.cdr.detectChanges();
    }

    productIsSelected = (product: DlmProductDto) =>
        this.productItems?.controls?.some(x => x.value.productId == product.dlmProductId) ||
        this.hireItems?.controls?.some(x => x.value.productId == product.dlmProductId);

    partIsSelected = (part: PartResponse) => this.partItems?.controls?.some(x => x.value.partId == part.id);
    loadcellIsSelected = (loadcell: LoadcellResponse) => this.calibrationItems?.controls?.some(x => x.value.loadcellId == loadcell.loadcellId);
    assetIsSelected = (asset: ProductDto) => this.hireItems?.controls?.some(x => x.value.productId == asset.productId);

    onPartSelected(itemControl: FormGroup, part: PartResponse) {
        itemControl.controls['cost'].setValue(part.cost ?? 0);
    }
    
    filterCompanies(event: any) {
        var text = event.target.value;
        this.filteredCompanies = this.companies.filter(m => m.name.toLowerCase().includes(text.toLowerCase()));
        this.cdr.detectChanges();
    }
    filterProducts(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageProducts(true);
    }
    filterParts(event: any) {
        // this.searchTerm = event.target.value;
        // this.page.pageIndex = 0;
        // this.pageParts(true);
        this.filteredParts = this.parts.filter(x => x.number.toLowerCase().includes(event.target.value.toLowerCase()));
    }
    filterLoadcells(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageLoadcells(true);
    }
    filterAssets(event: any) {
        this.searchTerm = event.target.value;
        this.page.pageIndex = 0;
        this.pageAssets(true);
    }
    filterMarineParts(event: any) {
        this.filteredMarineParts = this.marineParts.filter(x => x.partName.toLowerCase().includes(event.target.value.toLowerCase()));
    }
    filterMarinePartCategoriess(event: any) {
        this.filteredMarinePartCategories = this.marinePartCategories.filter(x => x.toLowerCase().includes(event.target.value.toLowerCase()));
    }

    getDiscountPercentage(itemControl: FormGroup, isHire: boolean = false) {
        var discount = itemControl.controls['discount'].value;
        var totalPrice = itemControl.controls['cost'].value * (isHire? this.getHireQuantity(itemControl) : itemControl.controls['quantity'].value);

        if(!totalPrice) return 0;

        return (100 * discount / totalPrice).toFixed(2);
    }

    updateDiscountFromPercentage(event: any, itemControl: FormGroup, isHire: boolean = false) {
        var discountPercentage = +(event.target.value) / 100;
        
        var totalPrice = itemControl.controls['cost'].value * (isHire? this.getHireQuantity(itemControl) : itemControl.controls['quantity'].value);

        itemControl.controls['discount'].setValue((totalPrice * discountPercentage).toFixed(2));

        //regular onchange autosave
        this.productsForm?.valid && this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    getOrderType = () => <number>this.getDealType();

    isCustomQuote = () => !!this.customerForm?.controls['isCustomQuote'].value;
    isRepeatCustomProduct = () => !!(this.customerForm?.controls['isRepeatCustomProduct']?.value ?? this.deal?.isRepeatCustomProduct ?? false);
    getDealType = () => <DealType>this.customerForm?.controls['dealType'].value;

    onCompanySelected(company: CustomerResponse) {
        this.customerForm.controls['companyId'].setValue(company.customerId);
        this.selectedCompanyName = company.name;
        this.cdr.detectChanges();
        this.regenTitle();

        if(this.customerForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    regenTitle() {
        var customerRef = this.customerForm?.controls['customerReqNo'].value || '';
        var quoteRef = this.deal?.quoteNumber || this.deal?.quoteRef || '';

        if(customerRef && quoteRef && this.selectedCompanyName) {
            var title = `${this.deal.quoteRef} ${this.selectedCompanyName} ${customerRef}`.trim();
            console.log("regen title:", title);
            this.customerForm.controls['name'].setValue(title);
        }

        return true;
    }

    getCompanyContacts() {
        var companyId = this.customerForm?.controls['companyId'].value;
        return (companyId ? this.contacts?.filter(x => x.customerId == companyId) : this.contacts) ?? [];
    }

    getCompanyInvoiceAddresses() {
        return this.getCompany()?.invoiceAddresses ?? [];
    }

    onCompanyInvoiceAddressChange(event: MatSelectChange) {
        var invoiceAddressId = event?.value;
        var invoiceAddress = this.getCompanyInvoiceAddresses()?.find(x => x.customerAddressId == invoiceAddressId);

        console.log("found address", invoiceAddress);

        if(invoiceAddress)
            this.summaryForm.controls['vatRate'].setValue(invoiceAddress.vatRate);
        
        if(this.customerForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    onMarginTypeChanged(event: MatSelectChange) {
        //prevent the "min" errors from the input
        if(event?.value == 0)
            this.summaryForm.controls['margin'].disable({emitEvent: false});
        else
            this.summaryForm.controls['margin'].enable({emitEvent: false});

        this.summaryForm.controls['margin'].updateValueAndValidity({onlySelf: false});
        
        if(this.summaryForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    getCompany() {
        var companyId = this.customerForm?.controls['companyId'].value;
        return companyId ? this.companies?.find(x => x.customerId == companyId) : null;
    }

    getContactEmail() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.email ?? '';
    }

    getContactPhoneNumber() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.phoneNumber ?? '';
    }

    getContactName() {
        var contactId = this.customerForm?.controls['contactId'].value;
        return this.contacts?.find(x => x.id == contactId)?.name;
    }

    lookupProductById(productId: number) {
        if(!productId)
            return null;

        return this.products?.find(x => x.dlmProductId == productId) ?? null;
    }
    lookupPartById(partId: number) {
        if(!partId)
            return null;

        return this.parts?.find(x => x.id == partId) ?? null;
    }
    lookupLoadcellById(loadcellId: number) {
        if(!loadcellId)
            return null;

        return this.loadcells?.find(x => x.loadcellId == loadcellId) ?? null;
    }
    lookupAssetById(assetId: number) {
        if(!assetId)
            return null;

        return this.assets?.find(x => x.productId == assetId) ?? null;
    }

    lookupProduct(itemControl: FormGroup) {
        var productId = itemControl.controls['productId'].value;
        return this.lookupProductById(productId);
    }
    lookupPart(itemControl: FormGroup) {
        var partId = itemControl.controls['partId'].value;
        return this.lookupPartById(partId);
    }
    lookupLoadcell(itemControl: FormGroup) {
        var loadcellId = itemControl.controls['loadcellId'].value;
        return this.lookupLoadcellById(loadcellId);
    }
    lookupAsset(itemControl: FormGroup) {
        var productId = itemControl.controls['productId'].value;
        return this.lookupAssetById(productId);
    }


    onCategoryToggled(event: MatOptionSelectionChange, cat: DealCategory) {
        if (!event?.isUserInput || !event.source.selected)
            return;
        
        // var cats : DealCategory[] = [cat, ...this.customerForm.controls['dealCategories'].value];

        // if(cat == DealCategory.StandardProducts) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Standard);
        // }
        // else if(cat == DealCategory.CustomProducts) {
        //     cats = cats.filter(x => x != DealCategory.StandardProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(true);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Custom);
        // }
        // else if(cat == DealCategory.RepeatCustomProduct) {
        //     cats = cats.filter(x => x != DealCategory.StandardProducts && x != DealCategory.CustomProducts);
        //     this.customerForm.controls['dealCategories'].setValue(cats);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(true);
        //     this.customerForm.controls['dealType'].setValue(DealType.RepeatCustom);
        // }
        // else if(cat == DealCategory.MarineSales) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Marine);
        // }
        // else if(cat == DealCategory.Hire) {
        //     cats = cats.filter(x => x != DealCategory.CustomProducts && x != DealCategory.RepeatCustomProduct);
        //     this.customerForm.controls['isCustomQuote'].setValue(false);
        //     this.customerForm.controls['isRepeatCustomProduct'].setValue(false);
        //     this.customerForm.controls['dealType'].setValue(DealType.Hire);
        // }

        switch(cat) {
            case DealCategory.StandardProducts:
                this.changeQuoteType(<any>{value: 0});
                break;
            case DealCategory.RepeatCustomProduct:
                this.changeQuoteType(<any>{value: 1});
                break;
            case DealCategory.CustomProducts:
                this.changeQuoteType(<any>{value: 2});
                break;
            //no calibration
            case DealCategory.Hire:
                this.changeQuoteType(<any>{value: 4});
                break;
            case DealCategory.MarineSales:
                this.changeQuoteType(<any>{value: 5});
                break;
        }

        if(this.customerForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    changeQuoteType(event: MatRadioChange) {
        this.customerForm.controls['dealType'].setValue(<DealType>event?.value);
        this.customerForm.controls['isCustomQuote'].setValue(event?.value == 2);
        this.customerForm.controls['isRepeatCustomProduct'].setValue(event?.value == 1);
        
        (this.productsForm.get('productLines') as FormArray).updateValueAndValidity({ onlySelf: true });
        (this.productsForm.get('partLines') as FormArray).updateValueAndValidity({ onlySelf: true });

        this.page.pageIndex = 0;

        if(this.isCustomQuote())
            this.pageParts(true);
        else if (this.getDealType() == DealType.Calibration)
            this.pageLoadcells(true);
        else if (this.getDealType() == DealType.Hire)
            this.pageAssets(true);
        else
            this.pageProducts(true);

        var cats = this.customerForm.controls['dealCategories'].value.filter((x: DealCategory) =>
            !(
                this.isCustomQuote() ? [DealCategory.StandardProducts, DealCategory.RepeatCustomProduct] :
                this.getDealType() == DealType.RepeatCustom ? [DealCategory.CustomProducts, DealCategory.StandardProducts] :
                [DealCategory.CustomProducts, DealCategory.RepeatCustomProduct]
            )
            .includes(x));

        cats.push(
            this.isCustomQuote() ? DealCategory.CustomProducts :
            this.getDealType() == DealType.RepeatCustom ? DealCategory.RepeatCustomProduct :
            this.getDealType() == DealType.Hire ? DealCategory.Hire :
            this.getDealType() == DealType.Marine ? DealCategory.MarineSales :
            DealCategory.StandardProducts
        );

        this.customerForm.controls['dealCategories'].setValue(getUniqueOnSelf(cats));
    }
    
    onMarinePartSelected(marinePart: MarinePartDto, itemControl: FormGroup) {
        itemControl.controls['partName'].setValue(marinePart.partName); //have to set this in order for the update to go through as triggers before change
        itemControl.controls['supplier'].setValue(marinePart.supplier);
        itemControl.controls['category'].setValue(marinePart.category);
        itemControl.controls['cost'].setValue(marinePart.price);
        itemControl.controls['internalCost'].setValue(marinePart.cost);

        if(this.productsForm.valid)
            this.save(true, false, this.deal?.isWizardInFirstStage);
    }

    getHireQuantity(itemControl: FormGroup) {
        var hireFrom = itemControl.controls['hireFrom'].value;
        var hireTo = itemControl.controls['hireTo'].value;

        if(!hireTo) return 1;
        else return TimeUtil.getDaysBetween(new Date(hireFrom), new Date(hireTo));
    }

    getHireQuantityFromItem(item: DealQuoteHireLineDto) {
        var hireFrom = item.hireFrom;
        var hireTo = item.hireTo;

        if(!hireTo) return 1;
        else return TimeUtil.getDaysBetween(new Date(hireFrom), new Date(hireTo));
    }

    getTotalPrice() {
        if(this.isCustomQuote())
            return this.getGrandTotal();
        else if (this.getDealType() == DealType.Calibration)
            return sumList(this.calibrationItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
        else if (this.getDealType() == DealType.Hire)
            return sumList(this.hireItems?.controls?.map(x =>
                (x.value.hireTo ? TimeUtil.getDaysBetween(new Date(x.value.hireFrom), new Date(x.value.hireTo)) : 1) * x.value.cost - x.value.discount));
        else if (this.getDealType() == DealType.Marine)
            return sumList(this.marineItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
        else
            return sumList(this.productItems?.controls?.map(x => x.value.quantity * x.value.cost - x.value.discount));
    }

    getMarginModifier() {
        var marginType = this.summaryForm?.controls['marginType'].value;
        var marginValue = this.summaryForm?.controls['margin'].value / 100;
        
        switch(marginType) {
            case 0: return 1;
            case 1: return marginValue;
            case 2: return marginValue + 1;
            default: return 1;
        }
    }


    buildModel() {
        var customerValue = this.customerForm.value as DealDto;
        var summaryValue = this.summaryForm.value as DealDto;

        var quote = Object.assign({}, this.deal, customerValue);


        quote.stageId ||= this.stages[0]?.stageId;


        quote.margin = summaryValue.margin ?? 0;
        quote.incoterm = summaryValue.incoterm;
        quote.freightTerm = summaryValue.freightTerm;
        quote.transportMode = summaryValue.transportMode;
        quote.freightCost = summaryValue.freightCost ?? 0;
        quote.vatRate = summaryValue.vatRate;

        quote.userId = this.appAuth.getUser()?.id ?? null;
        quote.value = this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value;
        quote.comments = [];

        // line items
        var productLines = [];
        var partLines = [];
        var calibrationLines = [];
        var hireLines = [];
        var marineLines = [];

        if(this.isCustomQuote())
            this.partItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuotePartLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                partLines?.push(item);
            });
        else if (this.getDealType() == DealType.Calibration)
            this.calibrationItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteCalibrationLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                calibrationLines?.push(item);
            });
        else if (this.getDealType() == DealType.Hire)
            this.hireItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteHireLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                hireLines?.push(item);
            });
        else if (this.getDealType() == DealType.Marine)
            this.marineItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteMarineLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                marineLines?.push(item);
            });
        else
            this.productItems?.controls?.forEach((itemControl: FormGroup) => {
                var item: DealQuoteProductLineDto = itemControl.value;
                item.dealId = this.dealId ?? 0;
        
                productLines?.push(item);
            });

        quote.quoteProductLines = productLines;
        quote.quotePartLines = partLines;
        quote.quoteCalibrationLines = calibrationLines;
        quote.quoteHireLines = hireLines;
        quote.quoteMarineLines = marineLines;

        quote.isWizardInProgress = true;

        return quote;
    }

    openPastOrders() {
        if(!this.selectedCompanyName) return;

        var getVatMultiplier = (vat: VatOption) => vat == VatOption.Reduced ? 1.05 : vat == VatOption.Standard ? 1.2 : 1;
        
        this.purchaseOrderService.apiPurchaseOrderGetAllGet$Json$Response({isSupplier: false, customer: this.selectedCompanyName, isDraft: false}).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data;

            this._infoDialogService.open({
                title  : 'Customer Order History',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div>
                            <div class="text-xl">${this.selectedCompanyName}</div>
                            <div class="">${purchaseOrders.length} previous orders found</div>
                        </div>
                        ${
                            purchaseOrders.map(po => `
                                <div class="border rounded-xl p-5 flex flex-col gap-2">
                                    <div class="flex justify-between">
                                        <div>
                                            <div class="text-xl">${po.salesOrderRef}</div>
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                        </div>
                                        <div>
                                            <div>Order Total</div>
                                            <div class="text-xl text-primary">${this.currencyPipe.transform(
                                                sumList(po.purchaseOrderHireItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat))) +
                                                sumList(po.purchaseOrderProductItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat))) +
                                                sumList(po.purchaseOrderPartItems.map(x => x.price * x.quantity * getVatMultiplier(x.vat)))
                                                , 'GBP')}</div>
                                        </div>
                                    </div>
                                    <div class="w-full border-b"></div>
                                    <div class="grid grid-cols-6 gap-2 text-sm uppercase">
                                        <div class="col-span-3">Description</div>
                                        <div>QTY</div>
                                        <div>Unit Price</div>
                                        <div>Total</div>
                                    </div>
                                    <div class="w-full border-b"></div>
                                    ${
                                        po.purchaseOrderProductItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    ${
                                        po.purchaseOrderHireItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    ${
                                        po.purchaseOrderPartItems.map(item => `
                                            <div class="grid grid-cols-6 gap-2">
                                                <div class="col-span-3">${item.description}</div>
                                                <div>${item.quantity}</div>
                                                <div>${this.currencyPipe.transform(item.price, 'GBP')}</div>
                                                <div>${this.currencyPipe.transform(item.quantity * item.price, 'GBP')}</div>
                                            </div>
                                        `).join('')
                                    }
                                    <div class="w-full border-b"></div>
                                    <div class="grid grid-cols-6 gap-2">
                                        <div class="col-span-3">VAT</div>
                                        <div></div>
                                        <div></div>
                                        <div>${this.currencyPipe.transform(
                                            sumList(po.purchaseOrderHireItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1))) +
                                            sumList(po.purchaseOrderProductItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1))) +
                                            sumList(po.purchaseOrderPartItems.map(x => x.price * x.quantity * (getVatMultiplier(x.vat) - 1)))
                                            , 'GBP'
                                        )}</div>
                                    </div>
                                </div>
                            `).join('')
                        }
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    openLoadcellOverview(loadcellId: number) {
        this.dialog.open(CellInformationDialogComponent, {
            panelClass: "cell-information-dialog",
            data: { loadcellId: loadcellId },
        });
    }

    openProductOverview(product: DlmProductDto) {
        var request = {
            PageSize: 9999,
            PageIndex: 0,
            IsSupplier: false,
            ProductId: product.dlmProductId,
        };
        
        this.purchaseOrderService.apiPurchaseOrderPaginatedListGet$Json$Response(request).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data.items;

            this._infoDialogService.open({
                title  : 'Product Overview',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div class="grid grid-cols-2 gap-y-4 gap-x-6">
                            <div>Product Number<br/>${product.partNumber}</div>
                            <div>Current Stock<br/>${product.freeStock} in stock</div>
                            <div class="col-span-2">Description<br/>${product.description}</div>
                        </div>

                        <div class="w-full border-b"></div>
                        <div>
                            <div class="text-xl">Sales History</div>
                            <div>${purchaseOrders.length} previous orders found</div>
                        </div>
                        
                        ${purchaseOrders.length ? `
                            <div class="border rounded-xl p-5 flex flex-col gap-2">
                                <div class="grid grid-cols-6 gap-2 text-sm uppercase">
                                    <div>Date</div>
                                    <div>Ref</div>
                                    <div class="col-span-2">Customer</div>
                                    <div>Quantity</div>
                                    <div>Total</div>
                                </div>
                                <div class="w-full border-b"></div>
                                ${
                                    purchaseOrders.map(po => `
                                        <div class="grid grid-cols-6 gap-2">
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                            <div>${po.salesOrderRef}</div>
                                            <div class="col-span-2">${po.company}</div>
                                            <div>${po.itemCount}</div>
                                            <div>${this.currencyPipe.transform(po.amount, 'GBP')}</div>
                                        </div>
                                    `).join('')
                                }
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>Total Sales<br/>${sumList(purchaseOrders.map(x => x.itemCount))}</div>
                                <div>Total Revenue<br/>${this.currencyPipe.transform(sumList(purchaseOrders.map(x => x.amount)), 'GBP')}</div>
                            </div>
                        ` : ``}
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    openAssetOverview(asset: ProductDto) {
        var request = {
            PageSize: 9999,
            PageIndex: 0,
            IsSupplier: false,
            ProductName: asset.name
        };
        
        this.purchaseOrderService.apiPurchaseOrderPaginatedListGet$Json$Response(request).subscribe(res => {
            if(!res?.body?.data) return;
            
            var purchaseOrders = res.body.data.items;

            this._infoDialogService.open({
                title  : 'Asset Overview',
                icon: {
                    show: false
                },
                panelClass: 'fuse-confirmation-dialog-panel-unset-w',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll flex flex-col gap-4 sm:-mr-8">
                        <div class="grid grid-cols-2 gap-y-4 gap-x-6">
                            <div>Asset Name<br/>${asset.name}</div>
                            <div>Current Stock<br/>${asset.stock} in stock</div>
                            <div class="col-span-2 whitespace-pre">Description<br/>${asset.description}</div>
                        </div>

                        <div class="w-full border-b"></div>
                        <div>
                            <div class="text-xl">Sales History</div>
                            <div>${purchaseOrders.length} previous orders found</div>
                        </div>
                        
                        ${purchaseOrders.length ? `
                            <div class="border rounded-xl p-5 flex flex-col gap-2">
                                <div class="grid grid-cols-5 gap-2 text-sm uppercase">
                                    <div>Date</div>
                                    <div>Ref</div>
                                    <div class="col-span-2">Customer</div>
                                    <div>Total</div>
                                </div>
                                <div class="w-full border-b"></div>
                                ${
                                    purchaseOrders.map(po => `
                                        <div class="grid grid-cols-5 gap-2">
                                            <div>${this.datePipe.transform(po.date, 'd MMM yyyy')}</div>
                                            <div>${po.salesOrderRef}</div>
                                            <div class="col-span-2">${po.company}</div>
                                            <div>${this.currencyPipe.transform(po.amount, 'GBP')}</div>
                                        </div>
                                    `).join('')
                                }
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div>Total Hires<br/>${purchaseOrders.length}</div>
                                <div>Total Revenue<br/>${this.currencyPipe.transform(sumList(purchaseOrders.map(x => x.amount)), 'GBP')}</div>
                            </div>
                        ` : ``}
                    </div>
                `,
                actions: {
                    confirm: {
                        show: true,
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
    
            this.cdr.detectChanges();
        });
    }

    sendToSales() {
        var salesTeam = this.allUsers.filter(x => x.teamRole == +TeamRole.Sales).map(x => <EnumViewModel> {value: x.id, name: `${x.firstName} ${x.lastName}`});

        //open sales dialog to set user
        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Assign to Sales Person",
                items: [
                    {name: 'sales person', propertyName: 'userId', type: 'select', options: salesTeam, initialValue: null, mandatory: true},
                ],
                additionalHtml: `
                    <div class="p-5 bg-[#f0f9ff] border border-[#bfdbfe] rounded-lg">
                        <div class="text-[#1e40af]">
                            <strong>Note:</strong> The assigned sales person will be responsible for reviewing the custom product requirements, finalizing pricing, and managing the customer relationship.
                        </div>
                    </div>
                `,
                overrideSaveText: 'Send to Sales',
            }
        });

        dialog.afterClosed().subscribe(async (dialogRes) => {
            if (!dialogRes) return;

            var form = this.buildModel();
            form.isWizardInProgress = true;
            form.userId = dialogRes.userId;
            form.isSentToSales = true;

            this.setStageToNegotiation(form);

            form = await this.saveDealApiAsync(form);

            //send notification (and email) to sales person
            this.dealService.apiDealSendSalesNotificationPut$Json$Response({body: form, userId: form.userId}).subscribe();

            //save BOM to "Custom Product"
            this.customProductName ||= 'Custom Product for ' + form.quoteRef;
            // var existingProduct = this.products.find(x => x.partNumber.toLowerCase() == this.customProductName.toLowerCase());
            var existingProduct = this.products.find(x => x.dlmProductId == this.bom?.productId);

            if(this.deal?.bomId && this.bom?.productId && existingProduct) {
                //Update existing BOM
                this.saveBom(form, existingProduct);
            }
            else { 
                //Create new custom product for BOM
                this.dlmProductService.apiDlmProductAddProductPost$Json$Response({
                    setGeneratedPartNumber: true,
                    body: {
                        partNumber: this.customProductPartNumber,
                        description: this.customProductName,
                        cost: this.getGrandTotal(),
                        price: this.getGrandTotal(), //add margins like in regular bom?
                        isCustomProduct: true,
                }}).subscribe(res => {
                    if(!res?.body?.data) return;

                    this.customProductPartNumber = res.body.data.partNumber;

                    //save BOM to new product
                    this.saveBom(form, res.body.data);
                });
            }
        });
    }

    saveBom(quote: DealDto, product: DlmProductDto) {
        this.buildBomModel(quote, product.dlmProductId);

        if(product.isCustomProduct)
            this.bom.isCustomProduct = true;

        if(this.deal?.bomId)
            this.bomService.apiBomUpdateBomPut$Json$Response({body: this.bom}).subscribe(res => {
                if(!res?.body?.data) return;
                this.close();
            });
        else
            this.bomService.apiBomAddBomPost$Json$Response({body: this.bom}).subscribe(res => {
                if(!res?.body?.data) return;
                quote.bomId = res.body.data.bomId;
                this.saveDealApi(quote, true);
            });
    }

    save(inProgress: boolean = true, close: boolean = true, isFirstStage: boolean = false) {
        var form = this.buildModel();

        form.isWizardInProgress = inProgress;
        form.isWizardInFirstStage = isFirstStage;

        if(!inProgress) {
            //send quote email to customer
            this.openQuoteEmail(form,
                () => {
                    this.setStageToNegotiation(form);
                    this.setPartsDescription(form);
                    this.saveDealApi(form, close);
                    this.createDealSalesOrder(form);
                },
                () => {
                    form.isWizardInProgress = true;
                });
        }
        else
            this.saveDealApi(form, close);
    }

    setStageToNegotiation(form: DealDto) {
        var negotiationStageId = this.stages.find(x => x.name.toLowerCase() == 'negotiation')?.stageId;
        if(negotiationStageId)
            form.stageId = negotiationStageId;
    }

    setPartsDescription(form: DealDto) {
        var dealParts = 'Items\n';
        var lineNumber = 1;

        if(form.quotePartLines.length) {
            var partLine = form.quotePartLines[0];
            dealParts += `Line ${lineNumber++}: Product ID ${this.customProductPartNumber}`;
            dealParts += `\n${this.customProductName}\n`;
            dealParts += [
                `Quantity: 1`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`,
                `Discount: £0.00`,
                `Subtotal: ${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`,
                `Lead Time: ${partLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var productLine of form.quoteProductLines) {
            dealParts += `Line ${lineNumber++}: Product ID ${this.lookupProductById(productLine.productId)?.partNumber}`;
            dealParts += `\n${this.lookupProductById(productLine.productId)?.description}\n`;
            
            dealParts += [
                `Quantity: ${productLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(productLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(productLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(productLine.cost * productLine.quantity - productLine.discount, 'GBP')}`,
                `Lead Time: ${productLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var hireLine of form.quoteHireLines) {
            dealParts += `Line ${lineNumber++}: Asset ID ${this.lookupAssetById(hireLine.productId)?.serialNumber}`;
            dealParts += `\n${this.lookupAssetById(hireLine.productId)?.name}\n`;
        
            dealParts += [
                `Quantity: ${this.getHireQuantityFromItem(hireLine)}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(hireLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(hireLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(hireLine.cost * this.getHireQuantityFromItem(hireLine) - hireLine.discount, 'GBP')}`,
                `Lead Time: ${hireLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var calibLine of form.quoteCalibrationLines) {
            dealParts += `Line ${lineNumber++}: Loadcell ID ${this.lookupLoadcellById(calibLine.loadcellId)?.bookingRef}`;
            dealParts += `\n${calibLine.description}\n`;
        
            dealParts += [
                `Quantity: ${calibLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(calibLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(calibLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(calibLine.cost * calibLine.quantity - calibLine.discount, 'GBP')}`,
                `Lead Time: ${calibLine.leadTime}\n\n`,
            ].join(' | ');
        }

        for(var marineLine of form.quoteMarineLines) {
            dealParts += `Line ${lineNumber++}: Part ${marineLine.partName}`;
            dealParts += `\n${marineLine.supplier}\n`;
            
            dealParts += [
                `Quantity: ${marineLine.quantity}`,
                `U/M: EA`,
                `Unit Price: ${this.currencyPipe.transform(marineLine.cost, 'GBP')}`,
                `Discount: ${this.currencyPipe.transform(marineLine.discount, 'GBP')}`,
                `Subtotal: ${this.currencyPipe.transform(marineLine.cost * marineLine.quantity - marineLine.discount, 'GBP')}`,
                `Lead Time: ${marineLine.leadTime}\n\n`,
            ].join(' | ');
        }

        //freight, vat, totals
        dealParts += `Totals\n`;
        dealParts += [
            `Subtotal: ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier(), 'GBP')}`,
            `Freight: ${this.currencyPipe.transform(this.summaryForm.controls['freightCost'].value, 'GBP')}`,
            `VAT (${this.summaryForm.controls['vatRate'].value * 100}%): ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * this.summaryForm.controls['vatRate'].value, 'GBP')}`,
            `Total (inc VAT): ${this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value, 'GBP')}`,
        ].join(' | ');

        console.log(dealParts.trim());
        form.partsDescription = dealParts.trim();
    }

    getTodaysDate = () => new Date();

    openQuoteEmail(deal: DealDto, successCallback: () => void, failureCallback: () => void) {
        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: "Send Email to Customer",
                items: [
                    {name: 'to', initialValue: this.getContactEmail(), mandatory: true},
                    {name: 'subject', initialValue: `Quote ${deal.quoteNumber || deal.quoteRef || ''} - ${this.appInfo.companyName}`, mandatory: true},
                    {name: 'body', type: 'textarea', initialValue:
                      `Dear ${this.getContactName()},\n\n` +
                      "I hope you are doing well.\n\n" +
                      `Please see attached your quote for ${this.selectedCompanyName} with reference ${deal.quoteNumber || deal.quoteRef || ''}.\n\n` +
                      "Thank you again for your order. Let us know if you have any questions or require further clarification.\n\n" +
                      "Best regards,\n" +
                      this.appInfo.companyName, mandatory: true},
                ],
                overrideSaveText: 'Send',
                overrideWidth: 65,
            }
        });

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) {
                failureCallback();
                return;
            }

            //get pdf (...)
            this.preparePdf(PDF => {
                //download the pdf locally (?)
                // PDF.save(`${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`);
                var blob = PDF.output('blob');

                //send on api
                this.dealService.apiDealSendCustomerQuoteEmailPost$Json$Response({emailTo: dialogRes.to, emailSubject: dialogRes.subject, emailBody: dialogRes.body,
                    fileName: `${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`,
                    body: {
                        formFile: blob
                    }
                }).subscribe(res => {
                    if(!res?.body?.data) {
                        //snack error todo
                        failureCallback();
                        return;
                    }

                    this.dealService.apiDealUploadFileDealIdPost$Json$Response({
                        dealId: this.dealId,
                        isBom: false,
                        filename: `${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`,
                        body: { formFile: blob },
                    }).subscribe(res => {
                        if(!res?.body?.data) {
                            failureCallback();
                            return;
                        }
                        successCallback();
                    });
                });
            });
        });
    }

    async saveDealApiAsync(deal: DealDto) {
        if(this.dealId) {
            var res = await this.dealService.apiDealUpdateQuoteDealPut$Json$Response({body: deal}).toPromise();
            if(!res?.body?.data) return;

            this.deal = res.body.data;
            this.dealId = this.deal.dealId;
            deal.dealId = this.dealId;
        }
        else {
            var res2 = await this.dealService.apiDealAddDealPost$Json$Response({body: deal}).toPromise();
            if(!res2?.body?.data) return;

            this.deal = res2.body.data;
            this.dealId = this.deal.dealId;
            deal.dealId = this.dealId;

            if(this.files.length) {
                var bomFiles = this.files.filter((_, i) => this.fileInfos[i].isBom);
                var nonBomFiles = this.files.filter((_, i) => !this.fileInfos[i].isBom);
                this.uploadImages(<any>bomFiles, true);
                this.uploadImages(<any>nonBomFiles, false);
            }
        }
        
        return this.deal;
    }

    saveDealApi(deal: DealDto, close: boolean = true) {
        if(this.dealId)
            this.dealService.apiDealUpdateQuoteDealPut$Json$Response({body: deal}).subscribe(res => {
                if(!res?.body?.data) return;

                this.deal = res.body.data;
                this.dealId = this.deal.dealId;
                deal.dealId = this.dealId;

                if(close)
                    this.close();
            });
        else
            this.dealService.apiDealAddDealPost$Json$Response({body: deal}).subscribe(res => {
                if(!res?.body?.data) return;

                this.deal = res.body.data;
                this.dealId = this.deal.dealId;
                deal.dealId = this.dealId;
                // this.buildForm();

                if(this.files.length) {
                    var bomFiles = this.files.filter((_, i) => this.fileInfos[i].isBom);
                    var nonBomFiles = this.files.filter((_, i) => !this.fileInfos[i].isBom);
                    this.uploadImages(<any>bomFiles, true);
                    this.uploadImages(<any>nonBomFiles, false);
                }

                if(close)
                    this.close();
            });
    }

    createDealSalesOrder(deal: DealDto) {
        var salesOrder = this.setupSalesOrderFromDeal(deal);
        this.purchaseOrderService.apiPurchaseOrderAddPurchaseOrderPost$Json$Response({body: salesOrder}).subscribe(res => {
            if (!res?.body?.data?.purchaseOrderId) return;

            var poId = res.body.data.purchaseOrderId;

            // Auto-generate pick sheet for the new SO
            this.pickSheetService.apiPickSheetGeneratePost$Json$Response({
                purchaseOrderId: poId
            }).subscribe();
        });
    }
    
    setupSalesOrderFromDeal(deal: DealDto) {
        var purchaseOrderProductItems = [];

        console.log("generating sales order...\ncustom", this.isCustomQuote(), "\nbomId", deal.bomId);

        if(this.isCustomQuote() && deal.bomId) {
            purchaseOrderProductItems = [
                {
                    description: this.lookupProductById(this.bom.productId)?.partNumber,
                    productId: this.bom.productId,
                    quantity: 1,
                    price: this.getGrandTotal(),
                    vat: deal.vatRate == 0.2 ? VatOption.Standard : deal.vatRate == 0.05 ? VatOption.Reduced : VatOption.Zero,
                }
            ];
        }
        else if(this.getDealType() == DealType.Calibration) {
            return; //todo
        }
        else if(this.getDealType() == DealType.Hire) {
            return; //todo
        }
        else if(this.getDealType() == DealType.Marine) {
            return; //todo
        }
        else {
            purchaseOrderProductItems = deal.quoteProductLines.map(x =>
                <PurchaseOrderProductItemDto>{
                    description: this.lookupProductById(x.productId)?.partNumber,
                    productId: x.productId,
                    quantity: x.quantity,
                    price: x.cost,
                    vat: deal.vatRate == 0.2 ? VatOption.Standard : deal.vatRate == 0.05 ? VatOption.Reduced : VatOption.Zero,
                }
            );
        }

        var purchaseOrder: PurchaseOrderDto = {
            contactId: deal.contactId,
            company: this.getCompany()?.name,
            isSupplier: false,
            isProduct: true,
            title: 'Sales Order for ' + (deal.quoteNumber || deal.quoteRef || ''),
            freightAmount: deal.freightCost,
            purchaseOrderHireItems: [],
            purchaseOrderPartItems: [],
            purchaseOrderProductItems: purchaseOrderProductItems,
            manufactureColumnDefinitionId: deal.manufactureColumnDefinitionId ?? null,
            dealId: this.dealId,
        };

        return purchaseOrder;
    }

    close(prompt: boolean = false) {
        if(prompt) {
            //prompt to save draft or discard


            
            this.dialogRef.close(null);
        }
        else
            this.dialogRef.close(null);
    }
    

    getCategoryName(cat: number) {
        switch(cat) {
            case DealCategory.StandardProducts: return 'Standard';
            case DealCategory.CustomProducts: return 'Custom';
            case DealCategory.CableWorkingGear: return 'Cable Working Gear';
            case DealCategory.MarineSales: return 'Marine';
            case DealCategory.NewLead: return 'New Lead';
            case DealCategory.RepeatCustomProduct: return 'Repeat Custom';
            default: return DealCategory[cat];
        }
    }

    getIncotermName(incoterm: number) {
        switch(incoterm) {
            case Incoterm.EXW: return 'EXW - Ex Works';
            // case Incoterm.FCA: return 'FCA - Free Carrier';
            // case Incoterm.FAS: return 'FAS - Free Alongside Ship';
            // case Incoterm.FOB: return 'FOB - Free On Board';
            // case Incoterm.CFR: return 'CFR - Cost and Freight';
            case Incoterm.CIF: return 'CIF - Cost, Insurance and Freight';
            // case Incoterm.CPT: return 'CPT - Carriage Paid To';
            // case Incoterm.CIP: return 'CIP - Carriage and Insurance Paid To';
            case Incoterm.DAP: return 'DAP - Delivered at Place';
            // case Incoterm.DPU: return 'DPU - Delivered At Place Unloaded';
            // case Incoterm.DDP: return 'DDP - Delivered Duty Paid';
            default: return Incoterm[incoterm];
        }
    }

    getFreightTermName(term: number) {
        switch(term) {
            case FreightTerm.DoorToDoor: return 'Door to Door';
            case FreightTerm.DoorToPort: return 'Door to Port';
            case FreightTerm.CustomerCollect: return 'Customer Collect';
            default: return FreightTerm[term];
        }
    }

    getTransportModeName(mode: number) {
        switch(mode) {
            default: return TransportMode[mode];
        }
    }
    
    getLeadTimeTypeName(type: number) {
        switch(type) {
            default: return LeadTimeType[type];
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // custom products x boms
    public customProductName: string = '';
    public customProductPartNumber: string = '0001-9999';


    // taken from BOM
    public PartUnitType = PartUnitType;
    
    private readonly componentText = 'Component';
    private readonly assemblyText = 'Assembly';
    private readonly subAssemblyText = 'Sub-Assy';

    isMultiLevel = () => true;

    getLevelIndent(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return 0;
        return [...level].filter(x => x == '.').length;
    }

    getNextLevel() {
        var allLevels = this.partItems?.controls
            .map((x: FormGroup) => x.controls['level']?.value ?? '')
            .filter((x: string) => x.length > 0)
            .map((x: string) => +x.split('.')[0])
            .filter(isNumber)
            ?? [];

        console.log("levels", allLevels);

        return !allLevels.length ? 1 : (Math.max(...allLevels) + 1);
    }

    hasLevelChildren(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return false;

        var levelChildPrefix = level + '.';

        var allLevels: string[] = this.partItems?.controls.map((x: FormGroup) => x.controls['level']?.value ?? '') ?? [];

        return allLevels.some(x => x.startsWith(levelChildPrefix));
    }

    toggleLevelChildrenHide(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return;

        var levelChildPrefix = level + '.';

        var childControls = this.partItems?.controls
        .filter((itemControl: FormGroup) => itemControl.controls['level']?.value?.startsWith(levelChildPrefix));

        if(!childControls?.length)
            return;

        //get first "hidden" state as nested children could have a mixture of these
        var currentToggleState = (childControls[0] as FormGroup).controls['hidden'].value;

        childControls.forEach((itemControl: FormGroup) => itemControl.controls['hidden'].setValue(!currentToggleState));
    }

    areLevelChildrenHidden(itemControl: FormGroup) {
        var level: string = itemControl.controls['level'].value;
        if(!level) return false;

        var levelChildPrefix = level + '.';

        var childControls = this.partItems?.controls
        .filter((itemControl: FormGroup) => itemControl.controls['level']?.value?.startsWith(levelChildPrefix));

        if(!childControls?.length)
            return false;

        return !!(childControls[0] as FormGroup).controls['hidden'].value;
    }

    reorderLevels() {
        if(!this.partItems?.controls)
            return;

        this.partItems.controls = this.partItems.controls.sort((a: FormGroup, b: FormGroup) =>
            (a.controls['level'].value > b.controls['level'].value) ? 1 :
            ((b.controls['level'].value > a.controls['level'].value) ? -1 : 0));
    }

    constrainLevelFormat(event, itemControl: FormGroup) {
        var regex = this.isMultiLevel() ? /[^0-9.]/g : /[^0-9]/g;

        event.target.value = event.target.value.replace(regex, '');

        itemControl.controls['level'].setValue(event.target.value);
    }


    getComponentType(itemControl: FormGroup) {
        if(!this.isMultiLevel())
            return this.componentText;

        //if level is a whole number it needs to be treated as a Sub Assembly (within Type) - this then should only populate 'S' part numbers
        var level = itemControl.get('level').value;

        if(!level.includes('.'))
            return this.assemblyText;
        else if(this.hasLevelChildren(itemControl))
            return this.subAssemblyText;
        else
            return this.componentText;
    }
    isAssemblyType = (itemControl: FormGroup) => this.getComponentType(itemControl) != this.componentText;

    // getComponentBgClass(itemControl: FormGroup) {
    //   var type = this.getComponentType(itemControl);

    //   return (type == this.assemblyText || type == this.subAssemblyText) ? 'bg-blue-100' : 'bg-gray-50';
    // }


    getItemSubtotal(itemControl: FormGroup) {
        var cost = itemControl?.get('cost').value ?? 0;
        var quantity = itemControl?.get('quantity').value ?? 1;
        
        return cost * quantity;
    }


    setupFilterParts (itemControl: FormGroup) {
      if(!this.lookupPart(itemControl))
        this.filteredParts = this.parts;
    }
  
    displayPart = (partId: number) => this.parts?.find(x => x.id == partId)?.number ?? '';
  
    showPartOption(part: PartResponse, itemControl: FormGroup) {
      return true; // take off filtering for now
  
  
      //if level is a whole number it needs to be treated as a Sub Assembly (within Type) - this then should only Populate 'S' part numbers
      if(!this.isMultiLevel())
        return true;
  
      return this.isAssemblyType(itemControl) == part.number.startsWith('S');
    }
  
    // getSubstitutes = (itemControl: FormGroup) => this.lookupPart(itemControl)?.substitutePartIds?.map(x => this.lookupPartFromId(x))?.filter(x => !!x);
    
    // isCheapestSubstitute(itemControl: FormGroup, part: PartResponseWithSubstitutes) {
    //   var subs = this.getSubstitutes(itemControl);
    //   var minCost = Math.min(...subs.map(x => x.cost));
    //   return part.cost == minCost;
    // }
  
    // selectSubstitute(itemControl: FormGroup, part: PartResponseWithSubstitutes) {
    //   itemControl.controls['partId'].setValue(part.id);
    // }

    public bom: BomDto;
    public bomAssemblyProcesses: any[] = [];

    
    // Assembly Process - Operation Codes
    public operationCodes = [
        { code: 'SUBCO', name: 'Sub-Contract' },
        { code: 'INSPE', name: 'Inspection' },
        { code: 'KITTI', name: 'Kitting' },
        { code: 'GAUGE', name: 'Gauge' },
        { code: 'OVEN', name: 'Oven' },
        { code: 'POSTC', name: 'Post Cure' },
        { code: 'WIRE', name: 'Wire' },
        { code: 'ASSEM', name: 'Assembly' },
        { code: 'PROOF', name: 'Proof Load' },
        { code: 'CALIB', name: 'Calibration' },
        { code: 'FINAL', name: 'Final Inspection' },
        { code: 'PACKI', name: 'Packing' },
        { code: 'MACHI', name: 'Machining' },
    ];

    // Assembly Process - Cost Centers
    public costCentres: CostCentreDto[] = [
        { code: '100TC', name: '100t Calibration Machine', rate: 0.00 },
        { code: '18CAL', name: '18T Calibration Machine', rate: 0.00 },
        { code: '300TC', name: '300T Calibration Machine', rate: 0.00 },
        { code: '3DPRT', name: '3D Printing', rate: 0.00 },
        { code: '60CAL', name: '60t Calibration Machine', rate: 0.00 },
        { code: '94TVU', name: '93t Vulcan Horizontal Test Machine', rate: 0.00 },
        { code: 'ANODI', name: 'Anodising', rate: 0.00 },
        { code: 'ASSEM', name: 'Assembly', rate: 0.00 },
        { code: 'BDMAR', name: 'BD Marine - Calibration', rate: 0.00 },
        { code: 'CURE', name: 'UV Cure', rate: 0.00 },
        { code: 'DURLI', name: 'Durham Lifting - Calibration', rate: 0.00 },
        { code: 'FINAL', name: 'Final Inspection', rate: 0.00 },
        { code: 'GAUGE', name: 'Gauge', rate: 0.00 },
        { code: 'HYJAW', name: 'Hydrajaws Ltd.', rate: 0.00 },
        { code: 'INSPE', name: 'Inspection', rate: 0.00 },
        { code: 'KITTI', name: 'Kitting', rate: 0.00 },
        { code: 'MACHI', name: 'Machining', rate: 0.00 },
        { code: 'NPL', name: 'National Physical Lab - London', rate: 0.00 },
        { code: 'OVEN', name: 'Oven', rate: 0.00 },
        { code: 'PACKI', name: 'Packing', rate: 0.00 },
        { code: 'POSTC', name: 'Post Cure', rate: 0.00 },
        { code: 'PRESS', name: 'Pressure Test Machine', rate: 0.00 },
        { code: 'PROOF', name: 'Proof Load', rate: 0.00 },
        { code: 'R&D', name: 'Research & Development', rate: 0.00 },
        { code: 'RFID', name: 'Fit and program RFID chip', rate: 0.00 },
        { code: 'STORE', name: 'Store 01', rate: 0.00 },
        { code: 'SUBCO', name: 'Sub-Contract', rate: 0.00 },
        { code: 'TRIFO', name: 'Tractel UK Ltd', rate: 0.00 },
        { code: 'WASH', name: 'Wash', rate: 0.00 },
        { code: 'WIRE', name: 'Wire', rate: 0.00 },
        { code: 'PROFI', name: 'Profiling', rate: 0.00 },
        { code: 'WELD', name: 'Welding', rate: 0.00 },
    ];

    addAssemblyProcess(process: any) {
        this.bomAssemblyProcesses.push(process);
        this.cdr.detectChanges();
      }
    
      deleteAssemblyProcess(index: number) {
        this.bomAssemblyProcesses.splice(index, 1);
        this.cdr.detectChanges();
        this.recalculateBomLeadTime();
      }
    
      onProcessDrop(event: CdkDragDrop<any[]>): void {
        moveItemInArray(
          this.bomAssemblyProcesses,
          event.previousIndex,
          event.currentIndex
        );
    
        // Update sequence numbers after reorder (in multiples of 10)
        this.bomAssemblyProcesses.forEach((process, index) => {
          process.sequence = (index + 1) * 10;
        });
    
        this.cdr.detectChanges();
      }
    
      getOperationName(code: string): string {
        return this.operationCodes.find(x => x.code === code)?.name || code;
      }
    
      getCostCentreName(code: string): string {
        return this.costCentres.find(x => x.code === code)?.name || code;
      }
    
      getProcessRate(process: any): number {
        if (process.isSubcontracted) {
          return process.subcontractorHourlyRate || 0;
        }
        return process.costCentreRate || 0;
      }
    
      getProcessTotal(process: any): number {
        const rate = this.getProcessRate(process);
        const totalHours = (process.setupHours || 0) * (process.isSubcontracted ? 35 : 1) + (process.runPerUnitHours || 0);
        return rate * totalHours;
      }
    
      getCostCentreProcesses() {
        return this.bomAssemblyProcesses.filter(p => !p.isSubcontracted);
      }
    
      getCostCentreProcessIndex(process: any): number {
        return this.bomAssemblyProcesses.indexOf(process);
      }
    
      getCostCentreProcessesTotal(): number {
        return this.getCostCentreProcesses().reduce((total, process) => {
          return total + this.getProcessTotal(process);
        }, 0);
      }
    
      getSubcontractorProcesses() {
        return this.bomAssemblyProcesses.filter(p => p.isSubcontracted && p.subcontractorName);
      }
    
      getSubcontractorProcessIndex(process: any): number {
        return this.bomAssemblyProcesses.indexOf(process);
      }
    
    getSubcontractorProcessTotal(process: any): number {
        const hourlyRate = process.subcontractorHourlyRate || 0;
        const totalHours = (process.setupHours || 0) * (process.isSubcontracted ? 35 : 1) + (process.runPerUnitHours || 0);
        return hourlyRate * totalHours;
    }
    
    getAssemblySubcontractorTotal(): number {
        return this.getSubcontractorProcesses().reduce((total, process) => {
            return total + this.getSubcontractorProcessTotal(process);
        }, 0);
    }
    
    getMaterialTotal() {
        var total = 0;
        this.partItems?.controls.forEach((itemControl: FormGroup) => total += this.getItemSubtotal(itemControl));
        return total;
    }
    
    getGrandTotal = () => this.getMaterialTotal() + this.getCostCentreProcessesTotal() + this.getAssemblySubcontractorTotal();

    
    openAssemblyProcessPopup(data: any = {}, isNew: boolean = true) {
        let title = isNew ? 'Add Process Step' : 'Update Process Step';
    
        // Auto-assign sequence for new processes
        if (isNew) {
        const maxSequence = this.bomAssemblyProcesses.length > 0
            ? Math.max(...this.bomAssemblyProcesses.map(p => p.sequence || 0))
            : 0;
        data.sequence = maxSequence + 10;
        }
    
        let dialogRef = this.dialog.open(AssemblyProcessPopupComponent, {
        disableClose: true,
            data: {
                title: title,
                payload: data,
                operationCodes: this.operationCodes,
                costCentres: this.costCentres
            }
        });
    
        dialogRef.afterClosed().subscribe((res) => {
            if (!res) return; // User cancelled
        
            if (isNew) {
                this.addAssemblyProcess(res);
                this.recalculateBomLeadTime();
            } else {
                // Update existing process
                const index = this.bomAssemblyProcesses.findIndex(p => p === data);
                if (index !== -1)
                    this.bomAssemblyProcesses[index] = res;
                this.recalculateBomLeadTime();
            }
            this.cdr.detectChanges();
        });
    }

    recalculateBomLeadTime() {
        var leadTime = sumList(this.bomAssemblyProcesses?.map(x => (x.setupHours ?? 0) * (x.isSubcontracted ? 35 : 1) + (x.runPerUnitHours ?? 0)) ?? []);
        var leadWeeks = TimeUtil.getWeeksNumber(leadTime);

        this.partItems?.controls?.forEach((itemControl: FormArray) => {
            itemControl.controls['leadTime'].setValue(leadWeeks);
            itemControl.controls['leadTimeType'].setValue(LeadTimeType.Weeks);
        });
    }


    buildBomModel(quote: DealDto, productId: number) {
        this.bom ??= {};

        this.bom.isNotDraft = true;
        this.bom.isPhantom = false;
        this.bom.creatorUserId = this.appAuth.getUser()?.id;
    
        this.bom.isMultiLevel = true;
        this.bom.leadTime = 0;
        this.bom.description = "Custom BOM for " + this.customProductName;
        this.bom.productId = productId;
        this.bom.revision = 1;
    
        this.bom.totalCost = this.getGrandTotal();
        
        // items
        this.bom.items = quote.quotePartLines.map(x => <BomItemDto> {
            bomId: this.bom.bomId ?? 0,
            level: x.level,
            partId: x.partId,
            quantity: x.quantity,
        });
    
        // assembly processes
        (this.bom as any).assemblyProcesses = this.bomAssemblyProcesses.map(process => {
            // Only strip bomId for new BOMs (bomId is 0 or null)
            if (!this.bom.bomId || this.bom.bomId === 0) {
                const { bomId, ...processWithoutBomId } = process;
                return processWithoutBomId;
            }
            // For existing BOMs, set the correct bomId
            return { ...process, bomId: this.bom.bomId };
        });
    }


    //pdf
    public downloadPdf() {
        this.preparePdf(PDF => {
            PDF.save(`${this.deal.quoteNumber || this.deal.quoteRef || ''}.pdf`);

            //move to negotiation
            var form = this.buildModel();
            form.isWizardInProgress = true;
            this.setStageToNegotiation(form);
            this.saveDealApiAsync(form);
        });
    }

    private preparePdf(callback: (pdfDoc: jsPDF) => any) {
        let PDF = new jsPDF("p", "mm", "a4", true);

        this.loader.open();

        //make sure loader fires before starting the pdf gen
        setTimeout(() => {

            //title
            this.addTextToPdf(PDF, [[`QUOTATION`]], 12, null, 50, null, true);
            this.addTextToPdf(PDF, [[`Reference: ${this.deal.quoteNumber || this.deal.quoteRef || ''}`]], 10, null, 56, null, true);

            var cellStyle: Partial<Styles> = { lineColor: "#000000", lineWidth: 0.2, cellPadding: {vertical: 1, horizontal: 2}, fontSize: 8, fontStyle: 'normal', };
            var blackCell: Partial<Styles> = { ...cellStyle, textColor: "#FFFFFF", fillColor: "#000000", cellWidth: 192, };
            var whiteCell: Partial<Styles> = { ...cellStyle, textColor: "#000000", fillColor: "#FFFFFF", cellWidth: 192, };
            var greyCell: Partial<Styles> = { ...cellStyle, textColor: "#000000", fillColor: "#e6e6e6", cellWidth: 38, };

            var blankCell: Partial<Styles> = { ...whiteCell, lineColor: '#FFFFFF', cellWidth: 32, fontSize: 9, cellPadding: {top: 3, bottom: 1, horizontal: 2} };

            var autoTableOptions: UserOptions = {theme: "grid", body: [], tableWidth: 192, margin: {horizontal: 8, top: 52, bottom: 36}, columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 64 }, 2: { cellWidth: 32 }, 3: { cellWidth: 64 }}, };


            //table 1 - specs
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "Customer Details", styles: {...blackCell, cellWidth: 96} },
                        { content: "Quote Details", styles: {...blackCell, cellWidth: 96} },
                    ],
                    [
                        { content: [
                                this.selectedCompanyName ?? '',
                                this.getContactEmail() ?? '',
                                this.getContactPhoneNumber() ?? '',
                                this.getCompany()?.address ?? '',
                            ].join('\n'), styles: {...whiteCell, cellWidth: 96} },
                        { content: [
                                `Title: ${this.customerForm?.controls['name'].value}`,
                                `Contact: ${this.getContactName()}`,
                                `Date: ${this.datePipe.transform(this.getTodaysDate(), 'd MMM yyyy')}`,
                            ].join('\n'), styles: {...whiteCell, cellWidth: 96} },
                    ],
                ],
            });


            //table 2+ - line items
            this.addTextToPdf(PDF, [['Items']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
            
            var lineItems: {isPart: boolean, itemControl: FormGroup}[] = [
                ...this.productItems?.controls.map(x => <any>{isPart: false, itemControl: x}),
                // ...this.partItems?.controls.map(x => <any>{isPart: true, itemControl: x}),
            ];

            lineItems.forEach((x, i) => {
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    ${x.isPart ? 'Part' : 'Product'} ID: ${x.isPart ? this.lookupPart(x.itemControl)?.number : this.lookupProduct(x.itemControl)?.partNumber}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${x.isPart ? this.lookupPart(x.itemControl)?.description : this.lookupProduct(x.itemControl)?.description}
                                
    Specification:
        • Quotation for ${x.isPart ? this.lookupPart(x.itemControl)?.description : this.lookupProduct(x.itemControl)?.description}
        • Product Code: ${x.isPart ? this.lookupPart(x.itemControl)?.number : this.lookupProduct(x.itemControl)?.partNumber}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${x.itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(x.itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            ...(x.isPart ? [] : [{ content: `Discount\n${this.currencyPipe.transform(x.itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell }]),
                            { content: `Lead Time\n${x.itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(x.itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(x.itemControl.controls['cost'].value * x.itemControl.controls['quantity'].value - (x.isPart ? 0 : x.itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            if(this.partItems?.length)
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: 1    Product ID: ${this.customProductPartNumber}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Specification:
        • Quotation for ${this.customProductName}
        • Product Code: ${this.customProductPartNumber}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${1}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${(this.partItems.controls[0] as FormArray).controls['leadTime'].value} ${this.getLeadTimeTypeName((this.partItems.controls[0] as FormArray).controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(this.getTotalPrice(), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });

            //calibration, hire, marine
            this.calibrationItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Loadcell ID: ${this.lookupLoadcell(itemControl)?.bookingRef}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${this.lookupLoadcell(itemControl)?.customerCode}
                                
    Specification:
        • Quotation for ${itemControl.controls['description'].value}
        • Product Code: ${this.lookupLoadcell(itemControl)?.serialNumber}
        • Calibrated to industrial standards`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * itemControl.controls['quantity'].value - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            this.hireItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Asset ID: ${this.lookupAsset(itemControl)?.serialNumber}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${this.lookupAsset(itemControl)?.name}
                                
    Specification:
        • Quotation for ${this.lookupAsset(itemControl)?.name}
        • Product Code: ${this.lookupAsset(itemControl)?.serialNumber}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${this.getHireQuantity(itemControl)}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * this.getHireQuantity(itemControl) - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ], //todo
                    ],
                });
            });

            this.marineItems?.controls.forEach((x, i) => {
                var itemControl = x as FormGroup;
                autoTable(PDF, {
                    ...autoTableOptions,
                    startY: ((PDF as any).lastAutoTable.finalY || 15) + 2,
                    head: [
                        [
                            { content: `Line: ${i+1}    Part: ${itemControl.controls['partName'].value}    Rev: 1`, styles: {...blackCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `${itemControl.controls['supplier'].value}
                                
    Specification:
        • Quotation for ${itemControl.controls['partName'].value}
        • Manufactured to industrial standards
        • Quality controlled and tested prior to dispatch
        • Full technical documentation included
        • Supplied with material certificates and test reports`, styles: {...whiteCell, cellWidth: 'auto'}, colSpan: 5 },
                        ],
                        [
                            { content: `Quantity\n${itemControl.controls['quantity'].value}`, styles: greyCell },
                            { content: `U/M\nEA`, styles: greyCell },
                            { content: `Unit Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value, 'GBP')}`, styles: greyCell },
                            { content: `Discount\n${this.currencyPipe.transform(itemControl.controls['discount'].value, 'GBP')}`, styles: greyCell },
                            { content: `Lead Time\n${itemControl.controls['leadTime'].value} ${this.getLeadTimeTypeName(itemControl.controls['leadTimeType'].value).toLowerCase()}`, styles: greyCell },
                            { content: `Total Price\n${this.currencyPipe.transform(itemControl.controls['cost'].value * itemControl.controls['quantity'].value - (itemControl.controls['discount'].value), 'GBP')}`, styles: {...greyCell, cellWidth: 40} },
                        ],
                    ],
                });
            });

            //table 3 - totals
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "Subtotal:", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier(), 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: "Freight:", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.summaryForm.controls['freightCost'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: `VAT (${this.summaryForm.controls['vatRate'].value * 100}%):`, styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * this.summaryForm.controls['vatRate'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                    [
                        { content: "Total (inc VAT):", styles: {...whiteCell, cellWidth: 144, halign: 'right'} },
                        { content: this.currencyPipe.transform(this.getTotalPrice() * this.getMarginModifier() * (1 + this.summaryForm.controls['vatRate'].value) + this.summaryForm.controls['freightCost'].value, 'GBP'), styles: {...whiteCell, cellWidth: 48} },
                    ],
                ],
            });

            
            this.addTextToPdf(PDF, [['Notes\nCustomer requires ATEX certification and CE marking']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);
            // this.addTextToPdf(PDF, [['Customer requires ATEX certification and CE marking']], null, 0, ((PDF as any).lastAutoTable.finalY || 15) + 1, null, null, 8, true, true);

            //table 4 - disclaimer
            autoTable(PDF, {
                ...autoTableOptions,
                startY: ((PDF as any).lastAutoTable.finalY || 15) + 4,
                head: [
                    [
                        { content: "This quotation is valid for 30 days from the date of issue. Prices are subject to change after this period.", styles: {...greyCell, cellWidth: 192, cellPadding: {vertical: 3, horizontal: 4}} },
                    ],
                ],
            });
            
            // Add header(s) and footer(s)
            this.addHeaderAndFooter(PDF).then(PDF => {
                this.loader.close();
                callback(PDF);
            });
            
        }, 100);

    }
  
  async addHeaderAndFooter(PDF: jsPDF) {
    for(var i=0; i < PDF.getNumberOfPages(); i++) {
      PDF.setPage(i);
      var canvas = await this.createCanvasFromElement("print-header");
      this.addCanvasToPdf(canvas, PDF, 0);
      var canvas2 = await this.createCanvasFromElement("print-footer");
      this.addCanvasToPdf(canvas2, PDF, 272);
    }
    return PDF;
  }


  //utils - can't multiple inherit
  createCanvasFromElement(elementId: string) {
    let element: any = document.getElementById(elementId);

    return html2canvas(element, {
      onclone: function (clonedDoc) {
        // make the element visible for the pdf
        clonedDoc.getElementById(elementId).style.display = "flex";
      },
    });
  }

  addCanvasToPdf(canvas: HTMLCanvasElement, PDF: jsPDF, position: number) {
    let fileWidth = 208;
    let fileHeight = (canvas.height * fileWidth) / canvas.width;

    const FILEURI = canvas.toDataURL("image/png");
    PDF.addImage(FILEURI, "PNG", 0, position, fileWidth, fileHeight);
  }

  setTableOptions(
      PDF: jsPDF,
      startY: number,
      columns: TableColumn[],
      values: RowInput[],
      columnStyles: { [key: string]: Partial<Styles> } = null,
      hasHeader: boolean = false,
      hasFooter: boolean = false,
      // headStyles: { [key: string]: Partial<Styles> } = null
    ): UserOptions {
      return {
        head: [columns.map((x) => x.name)],
        body: values,
        theme: "plain",
        startY: startY,
        margin: {
          top: hasHeader ? 50 : 10,
          bottom: hasFooter ? 30 : 5,
          left: 14,//2.5,
          right: 14,//3.75,
        },
        styles: {
          fontSize: 8,
        },
        columnStyles: columnStyles,
        //   headStyles: headStyles,
        pageBreak: 'avoid',
        willDrawCell: (data) => {
          PDF.setDrawColor("#cccccc");
          PDF.setLineWidth(0.2);
          PDF.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height
          );
        },
        tableLineColor: "black",
        tableLineWidth: 0, //0.2
        didParseCell: (data) => {
          if (columnStyles && data.cell && data.cell.section === "head") {
            var colIndex = columns.findIndex((x) => x.name == data.cell.raw);
  
            //set header styles to match the column styles here
            if (columnStyles[colIndex]) {
              data.cell.styles.halign = columnStyles[colIndex].halign;
              data.cell.styles.cellWidth = columnStyles[colIndex].cellWidth;
              // data.cell.styles.fontSize = columnStyles[colIndex].fontSize;
              // data.cell.styles.cellPadding = columnStyles[colIndex].cellPadding;
            }
          }
        },
      };
    }

  addTextToPdf(
    PDF: jsPDF,
    data: RowInput[],
    fontSize?: number,
    padding?: number,
    startY?: number,
    offset?: number,
    center?: boolean,
    tableMargin?: MarginPaddingInput,
    hasHeader: boolean = false,
    hasFooter: boolean = false,
  ) {
      var columns = [
          {name: ''},
      ];
      var options = this.setTableOptions(
          PDF,
          (startY ?? ((PDF as any).lastAutoTable.finalY || 15) + (offset ?? 5)),
          columns,
          data,
          null,
          hasHeader,
          hasFooter,
      );
      options.styles = {
          fontSize: fontSize ?? 9,
          cellPadding: padding ?? 0.5,
          halign: center == true ? 'center' : 'left',
      };
      options.willDrawCell = (data) => {
          PDF.setDrawColor("#ffffff");
          PDF.setLineWidth(0);
          PDF.line(0,0,0,0);
      };
      if(tableMargin != null)
        options.margin = tableMargin;
      autoTable(PDF, options);
  }


    // Files
    loadFiles() {
        if(!this.dealId) return;

        this.dealService.apiDealGetFilesGet$Json({ dealId: this.dealId }).subscribe(res => {
            if(!res?.data) return;

            this.fileInfos = res.data;

            this.cdr.detectChanges();
        });
    }

    // async openFileUpload(){
    //     var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

    //     dialog.afterClosed().subscribe((dialogRes) => {
    //         if (!dialogRes) return;

    //         this.uploadImages(dialogRes);
    //     });    
    // }

    openFileUpload(isBom: boolean = false) {
        var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            if(this.dealId) {
                this.uploadImages(dialogRes, isBom);
                return;
            }
            
            for(var file of dialogRes) {
                var extension = '.' + file.name.split('.').pop();
                this.files.push(file);

                this.fileInfos.push({ 
                    dealId: this.dealId, 
                    name: file.name, 
                    type: extension,
                    isBom: isBom,
                });
            }
        });    
    }

    deleteFile(file: QuoteFileDto, event: any){
        event.stopPropagation();

        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title  : "Delete File",
            message: 'Are you sure you want to delete this file?',
            actions: {
                confirm: {
                    label: "Delete",
                },
            },
        });
    
        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if ( result === 'confirmed' )
            {
                if(this.dealId)
                    this.dealService.apiDealDeleteFileFileIdDelete$Json({ fileId: file?.quoteFileId }).subscribe((res) => {
                        if(!res) return;
            
                        this.loadFiles();
                    });
                else {
                    this.fileInfos = this.fileInfos.filter(x => x.name != file.name);
                    this.files = this.files.filter(x => x.name != file.name);
                }
            }
        });
    }

    async uploadImages(files: FileList, isBom: boolean) {
        if (!this.dealId) return;
        
        var newFiles = Array.from(files);

        newFiles
            .forEach((file) => {
                var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
                var blob: Blob = file;
                var name: string = file.name;

                this.dealService.apiDealUploadFileDealIdPost$Json({ 
                    dealId: this.dealId,
                    filename: name,
                    isBom: isBom,
                    body: { formFile: blob },
                }).subscribe((res) => {
                    this.loadFiles();
                });
            });
    }

    download(file: QuoteFileDto){
        this.dealService.apiDealDownloadFileFileIdGet({ fileId: file.quoteFileId }).subscribe((res) => {
            if(!res) return;

            // Convert the response to a Blob
            const blob = new Blob([res], { type: 'application/octet-stream' });

            // Create a link element
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // if the items name doesnt end with the file extension, add it
            if(!file?.name.endsWith(file?.type)){
                file.name += file?.type;
            }

            // Set the download attribute of the link to the desired file name
            link.download = file.name;
            // link.target = '_blank';

            // window.open(url);

            // Simulate a click on the link
            link.click();

            // Release the reference to the blob
            window.URL.revokeObjectURL(url);
        });
    }

    getDaysInSwimlane() {
        if(!this.dealId || !this.deal || !this.deal.dateSinceSwimlaneChange) return null;

        var date = new Date(this.deal.dateSinceSwimlaneChange);
        return !date ? '0 hrs' : TimeUtil.getDaysIntervalText(date, TimeUtil.getNowUTC());
    }

    getSlaDays() {
        if(!this.dealId || !this.deal || !this.deal.dateSinceSwimlaneChange) return null;

        var currentStage = this.stages.find(x => x.stageId = this.deal.stageId);
        if(!currentStage) return null;

        var slaDays = null;
        
        /*
            New Enquiry - 2 working days 
            Follow Up - 1 working day 
            Negotiation - no timer 
            Quote - standard tracking only 
        */
        switch(currentStage.name.toLowerCase().trim()) {
            case 'new enquiry':
                slaDays = 2;
                break;
            case 'follow up':
                slaDays = 1;
                break;
            case 'negotiation':
            case 'quotation':
                break;
        }

        return slaDays;
    }

    calculateSLA() {
        var slaDays = this.getSlaDays();
        if(slaDays == null) return;

        var date = new Date(this.deal.dateSinceSwimlaneChange);

        this.dealService.apiDealAddWorkingDaysGet$Json$Response({date: date.toISOString(), days: slaDays}).subscribe(res => {
            if(!res?.body) return;
            this.slaDate = new Date(res.body);
        });
        
    }
}
